import { Link, useNavigate } from 'react-router';

import { useMigrations } from 'src/containers/Migrations';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Card, CardDescription, CardTitle } from 'src/components/ui/card';
import env from 'env';

type IMigrationStep = {
  step: number;
  title: React.ReactNode;
  description: React.ReactNode;
};

function MigrationStep({ step, title, description }: IMigrationStep) {
  return (
    <Card className="bg-accent flex items-start gap-3 p-6">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-foreground-foreground">
        {step}
      </span>
      <div className="space-y-2 grow">
        <CardTitle className="text-sm">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </div>
    </Card>
  );
}

export default function InfoModal() {
  const navigate = useNavigate();
  const {
    ui: { isInfoModalVisible },
    setIsInfoModalVisible,
  } = useMigrations();

  const onAddIntegration = () => {
    setIsInfoModalVisible(false);
    navigate('/integrations');
  };

  return (
    <Dialog open={isInfoModalVisible} onOpenChange={setIsInfoModalVisible}>
      <DialogContent className="sm:max-w-[600px] gap-6">
        <DialogHeader className="gap-1.5">
          <DialogTitle className="text-xl font-bold">Migrate your assets</DialogTitle>
          <DialogDescription>
            We&apos;ve updated the connection method between Codekeeper and your source code management (SCM) platform
            to OAuth integration. Some of your assets are affected and need to be migrated.
            <br />
            <br />
            To migrate to OAuth integration, follow these steps:
          </DialogDescription>
        </DialogHeader>

        <MigrationStep
          step={1}
          title={
            <>
              Add an{' '}
              <Link to="/integrations" className="text-foreground! underline!">
                integration
              </Link>
            </>
          }
          description="Connect your SCM platform to Codekeeper from the Integrations page, or use the button below."
        />
        <MigrationStep
          step={2}
          title="Migrate your assets"
          description="Go to the Migrations page to migrate the assets listed above. You can select multiple assets from the same SCM platform at once."
        />
        <MigrationStep
          step={3}
          title="Deposits and labeling"
          description={
            <>
              Once migrated, your assets will appear on the <strong>Software Escrow {'>'} Deposits</strong> tab. The
              first deposit with the new integration may take up to 24 hours. Open the escrow to label your assets.
            </>
          }
        />

        <DialogDescription>
          Your current connection method and deposited assets will continue to work until you complete the migration.
          <br />
          <br />
          Please contact{' '}
          <a href={`mailto:${env.CODEKEEPER_SERVICE_EMAIL}`} className="text-foreground! underline!">
            support
          </a>{' '}
          if you have any questions.
        </DialogDescription>

        <DialogFooter className="sm:justify-center">
          <Button onClick={onAddIntegration}>Create integration</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
